import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Payment Applications RPC Contract
 *
 * Lists payment applications (cash applied to an invoice) by the cash receipt
 * they spend or the invoice they pay, and applies a receipt by hand.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Pagination schema
 */
export const ArPaymentApplicationPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * List AR payment applications input schema
 */
export const ListArPaymentApplicationsInputSchema = z.object({
  /** The cash receipt the applications spend (GEN-3201). */
  bankTransactionId: UUIDSchema.optional(),
  invoiceId: UUIDSchema.optional(),
  applicationDateFrom: z.string().optional(),
  applicationDateTo: z.string().optional(),
  sortBy: z.enum(["applicationDate", "appliedAmount"]).optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * The cash receipt inside a payment application list item
 */
export const ArPaymentApplicationReceiptSummarySchema = z.object({
  id: UUIDSchema,
  referenceNumber: z.string().nullable(),
  transactionDate: z.string().nullable(),
  amount: DecimalStringSchema.nullable(),
  payerName: z.string().nullable(),
});

/**
 * Invoice summary inside payment application list item
 */
export const ArPaymentApplicationInvoiceSummarySchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string(),
  netAmount: DecimalStringSchema.nullable(),
  balanceDue: DecimalStringSchema.nullable(),
  status: z.string(),
});

/**
 * AR payment application list item schema
 */
export const ArPaymentApplicationListItemSchema = z.object({
  id: UUIDSchema,
  bankTransactionId: UUIDSchema.nullable(),
  invoiceId: UUIDSchema,
  appliedAmount: DecimalStringSchema.nullable(),
  applicationDate: z.string().nullable(),
  sequenceNumber: z.number().nullable(),
  /** Who made it: only `user` applications can be removed by hand. */
  allocationSource: z.enum([
    "user",
    "remittance_ocr",
    "erp_import",
    "auto_match",
  ]),
  receipt: ArPaymentApplicationReceiptSummarySchema.nullable(),
  invoice: ArPaymentApplicationInvoiceSummarySchema.nullable(),
});

/**
 * List AR payment applications response schema
 */
export const ListArPaymentApplicationsResponseSchema = z.object({
  paymentApplications: z.array(ArPaymentApplicationListItemSchema),
  pagination: ArPaymentApplicationPaginationSchema,
  /** Sum of `appliedAmount` over every matching row, not just this page. */
  totalAppliedAmount: DecimalStringSchema,
});

/**
 * Create (manually apply a cash receipt to an invoice).
 */
/** Decimal string that must represent a strictly-positive amount. */
const PositiveDecimalStringSchema = DecimalStringSchema.refine(
  (v) => Number(v) > 0,
  { message: "Applied amount must be greater than 0" },
);

export const CreateArPaymentApplicationInputSchema = z.object({
  /** The cash receipt applied (GEN-3202). */
  bankTransactionId: UUIDSchema,
  invoiceId: UUIDSchema,
  appliedAmount: PositiveDecimalStringSchema,
  applicationDate: z.string().nullable().optional(),
});

export const CreateArPaymentApplicationResponseSchema = z.object({
  id: UUIDSchema,
  bankTransactionId: UUIDSchema,
  invoiceId: UUIDSchema,
});

/**
 * Delete (remove a manually-created allocation).
 */
export const DeleteArPaymentApplicationInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteArPaymentApplicationResponseSchema = z.object({
  success: z.literal(true),
  invoiceId: UUIDSchema,
});

/**
 * AR Payment Applications RPC Contract
 */
export const arPaymentApplicationsContracts = {
  list: oc
    .input(ListArPaymentApplicationsInputSchema)
    .output(ListArPaymentApplicationsResponseSchema)
    .route({
      method: "GET",
      summary: "List payment applications",
      description:
        "Paginated allocations of cash receipts to invoices, filterable by receipt, invoice, and application date. Each row shows applied amount with receipt and invoice summaries, plus the total applied across every matching row.",
    }),
  create: oc
    .input(CreateArPaymentApplicationInputSchema)
    .output(CreateArPaymentApplicationResponseSchema)
    .route({
      method: "POST",
      summary: "Apply a cash receipt to an invoice",
      description:
        "Manually allocates a positive amount of a cash receipt to an invoice, optionally dated, recorded as a user allocation.",
    }),
  delete: oc
    .input(DeleteArPaymentApplicationInputSchema)
    .output(DeleteArPaymentApplicationResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a manual payment application",
      description:
        "Deletes a user-created receipt-to-invoice allocation. Allocations from remittances or ERP sync cannot be removed. Returns the affected invoice id.",
    }),
} as const;

// Type exports
export type ArPaymentApplicationPagination = z.infer<
  typeof ArPaymentApplicationPaginationSchema
>;
export type ListArPaymentApplicationsInput = z.infer<
  typeof ListArPaymentApplicationsInputSchema
>;
export type ListArPaymentApplicationsResponse = z.infer<
  typeof ListArPaymentApplicationsResponseSchema
>;
export type ArPaymentApplicationListItem = z.infer<
  typeof ArPaymentApplicationListItemSchema
>;
export type ArPaymentApplicationReceiptSummary = z.infer<
  typeof ArPaymentApplicationReceiptSummarySchema
>;
export type ArPaymentApplicationInvoiceSummary = z.infer<
  typeof ArPaymentApplicationInvoiceSummarySchema
>;
export type CreateArPaymentApplicationInput = z.infer<
  typeof CreateArPaymentApplicationInputSchema
>;
export type CreateArPaymentApplicationResponse = z.infer<
  typeof CreateArPaymentApplicationResponseSchema
>;
export type DeleteArPaymentApplicationInput = z.infer<
  typeof DeleteArPaymentApplicationInputSchema
>;
export type DeleteArPaymentApplicationResponse = z.infer<
  typeof DeleteArPaymentApplicationResponseSchema
>;
